/**
 * JHA emergency map & directions.
 *
 * Shows (a) the device location via the browser geolocation prompt (optional —
 * a denial degrades to a gentle hint, never a failure), (b) a pin for the
 * project job-site coordinates, (c) pins for nearby hospitals / urgent care
 * from the EXISTING server emergency-contacts lookup
 * (GET /api/jha/emergency-contacts/:projectId — provider-backed, coordinates
 * already part of the response; no new lookup was built).
 * (d) a Google Maps directions deep link (https://www.google.com/maps/dir/?api=1&destination=LAT,LNG)
 * to the nearest hospital/urgent care — no key, no Directions API, opens in a
 * new tab, one-handed on a phone.
 *
 * Honesty rules (product standard — every failure says WHY and HOW to fix it):
 *  - The live Google map renders ONLY when VITE_GOOGLE_MAPS_API_KEY is set.
 *    Without it we show the static emergency text and a short note that the
 *    live map is not configured — no fake map, no fabricated data.
 *  - Provider failures (lookup error / configured:false) are shown loudly with
 *    the server error code; static text from the saved JHA emergencyInfo still
 *    renders because a life-safety item must never depend on a map.
 *  - The static nearest-facility card (name / address / phone / 911) is
 *    independent of the map and of any key.
 */
import { useEffect, useRef, useState } from 'react';
import { MapPin, Navigation, Phone, AlertTriangle, Map as MapIcon } from 'lucide-react';
import { api } from '@/utils/api';
import { loadGoogleMaps } from '@/utils/jhaGoogleMaps';

export const DIRECTIONS_URL_BASE = 'https://www.google.com/maps/dir/';

/** Shape of the fields we consume from the existing server result. */
export interface EmergencyFacility {
  name: string | null;
  address: string | null;
  phone: string | null;
  latitude: number | null;
  longitude: number | null;
}

interface EmergencyContactsResponse {
  configured: boolean;
  coordinates: { latitude: number; longitude: number } | null;
  hospital: EmergencyFacility | null;
  clinic: EmergencyFacility | null;
  error: { code: string; message: string } | null;
  /** DELTA2-02: server flag — every category lookup returned no facility. */
  allLookupsEmpty?: boolean;
  /** DELTA2-02: server flag — at least one category lookup failed at the provider. */
  providerDegraded?: boolean;
  /** UAT-006: server-reported geocode match precision. */
  siteGeocodeConfidence?: string | null;
  /** UAT-006 fail closed: medical results existed but NONE passed medical-facility validation. */
  nonMedicalResults?: boolean;
}

interface SavedFacility {
  name?: string;
  address?: string;
  phone?: string;
}

export interface JhaEmergencyMapProps {
  projectId: number;
  /** Saved (offline) emergency info from the JHA record — the fallback truth. */
  emergencyInfo?: {
    hospital?: SavedFacility;
    clinic?: SavedFacility;
  };
}

type LookupState =
  | { kind: 'loading' }
  | { kind: 'provider-error'; code: string; message: string }
  | { kind: 'loaded'; data: EmergencyContactsResponse };

type GeoState = 'pending' | 'granted' | 'denied' | 'unavailable';

/** JHA-MED-01: Google Maps needs LatLngLiteral { lat, lng }, never { latitude, longitude }. */
export function toLatLngLiteral(c: { latitude: number; longitude: number }): { lat: number; lng: number } {
  return { lat: c.latitude, lng: c.longitude };
}

/** NEW-JHA-05: only a finite, in-range numeric pair counts as a real site fix. */
export function isValidGeoPoint(c: unknown): c is { latitude: number; longitude: number } {
  if (!c || typeof c !== 'object') return false;
  const { latitude, longitude } = c as { latitude: unknown; longitude: unknown };
  return (
    typeof latitude === 'number' && Number.isFinite(latitude) && latitude >= -90 && latitude <= 90 &&
    typeof longitude === 'number' && Number.isFinite(longitude) && longitude >= -180 && longitude <= 180
  );
}

/** NEW-JHA-06: a whitespace-only name is not a usable facility name. */
export function hasUsableName(f: { name?: string | null } | null | undefined): boolean {
  return !!f?.name?.trim();
}

/**
 * UAT-006 (CRITICAL, fail closed — client mirror of the server gate): a
 * provider once returned a skin-design business as the "nearest emergency
 * facility". A category search is not proof of a medical facility. Only a
 * name with a positive medical signal and no non-medical marker may be
 * presented as emergency safety data; anything else is withheld and the user
 * is told to verify manually. Mirrors the server's
 * emergency-contacts.service isPlausibleMedicalFacilityName.
 */
const NON_MEDICAL_NAME_MARKERS = [
  'spa', 'salon', 'skin', 'tattoo', 'nail', 'waxing', 'massage', 'beauty',
  'hair', 'barber', 'lash', 'brow', 'esthetic', 'aesthetic', 'cosmetic',
  'boutique', 'tanning', 'gym', 'fitness', 'yoga', 'pilates', 'crossfit',
  'restaurant', 'bar & grill', 'coffee', 'pizza',
];
const POSITIVE_MEDICAL_NAME_MARKERS = [
  'hospital', 'medical', 'clinic', 'urgent care', 'emergency room', 'emergency dept',
  'health', 'doctor', 'physician', 'surgical', 'surgery', 'trauma', 'er &',
  'healthcare', 'health care', 'med center', 'medical center', 'infusion',
];
export function isPlausibleMedicalFacilityName(name: unknown): boolean {
  const raw = typeof name === 'string' ? name.trim().toLowerCase() : '';
  if (raw === '') return false;
  if (NON_MEDICAL_NAME_MARKERS.some((m) => raw.includes(m))) return false;
  return POSITIVE_MEDICAL_NAME_MARKERS.some((m) => raw.includes(m));
}

/** Great-circle distance in miles (haversine). Exported for tests. */
export function haversineMiles(
  a: { latitude: number; longitude: number },
  b: { latitude: number; longitude: number }
): number {
  const R = 3958.8;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.latitude - a.latitude);
  const dLon = toRad(b.longitude - a.longitude);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.latitude)) * Math.cos(toRad(b.latitude)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

/**
 * Nearest hospital/urgent-care facility we can honestly name.
 * JHA-HIGH-01: when both categories returned a facility WITH coordinates and
 * the site coordinates are known, compare real distances — provider ranking is
 * nearest-first only WITHIN a category and never proved hospital-over-clinic.
 */
function nearestMedicalFacility(
  live: EmergencyContactsResponse | null,
  saved: JhaEmergencyMapProps['emergencyInfo']
): { facility: EmergencyFacility | null; droppedNonMedical: boolean } {
  // UAT-006 fail closed: every candidate must ALSO pass the medical-facility
  // name check — a provider/category mislabel is never named as a facility.
  const candidates = [live?.hospital, live?.clinic].filter(
    (f): f is EmergencyFacility => !!f && hasUsableName(f) && isPlausibleMedicalFacilityName(f.name)
  );
  const anyCandidateDropped = [live?.hospital, live?.clinic, saved?.hospital, saved?.clinic]
    .filter((f): f is { name?: string | null } => !!f && hasUsableName(f as any))
    .some((f) => !isPlausibleMedicalFacilityName(f.name));
  if (candidates.length > 0) {
    const site = live?.coordinates ?? null;
    const withCoords = site
      ? candidates.filter((f) => f.latitude !== null && f.longitude !== null)
      : [];
    const facility =
      site && withCoords.length > 0
        ? withCoords.reduce((best, f) =>
            haversineMiles(site, { latitude: f.latitude!, longitude: f.longitude! }) <
            haversineMiles(site, { latitude: best.latitude!, longitude: best.longitude! })
              ? f
              : best
          )
        : candidates[0];
    return { facility, droppedNonMedical: anyCandidateDropped };
  }
  const savedFacility = saved?.hospital ?? saved?.clinic ?? null;
  if (hasUsableName(savedFacility) && isPlausibleMedicalFacilityName(savedFacility.name)) {
    return {
      facility: {
        name: savedFacility.name ?? null,
        address: savedFacility.address ?? null,
        phone: savedFacility.phone ?? null,
        latitude: null,
        longitude: null,
      },
      droppedNonMedical: anyCandidateDropped,
    };
  }
  return { facility: null, droppedNonMedical: anyCandidateDropped };
}

export function directionsUrl(facility: EmergencyFacility | null): string | null {
  if (!facility) return null;
  if (facility.latitude !== null && facility.longitude !== null) {
    return `${DIRECTIONS_URL_BASE}?api=1&destination=${facility.latitude},${facility.longitude}`;
  }
  if (facility.address) {
    return `${DIRECTIONS_URL_BASE}?api=1&destination=${encodeURIComponent(facility.address)}`;
  }
  return null;
}

export function JhaEmergencyMap({ projectId, emergencyInfo }: JhaEmergencyMapProps) {
  // Read at render time (not module scope) so tests can stub the env per case.
  const mapsKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
  const [lookup, setLookup] = useState<LookupState>({ kind: 'loading' });
  const [geo, setGeo] = useState<GeoState>('pending');
  // JHA-MED-02: the granted fix is kept and rendered as its own map marker.
  const [devicePos, setDevicePos] = useState<{ lat: number; lng: number } | null>(null);
  const [mapState, setMapState] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const mapDivRef = useRef<HTMLDivElement | null>(null);
  const mapInstanceRef = useRef<any>(null);
  const mapsApiRef = useRef<any>(null);
  const deviceMarkerRef = useRef<any>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await api.get(`/jha/emergency-contacts/${projectId}`);
        const data = response.data?.data as EmergencyContactsResponse | undefined;
        if (cancelled) {
          return;
        }
        if (!data) {
          setLookup({ kind: 'provider-error', code: 'EMPTY_RESPONSE', message: 'The emergency-contacts endpoint returned no data.' });
          return;
        }
        // Luna delta4 F-01: a real geocode failure arrives as error
        // SITE_NOT_GEOCODED — that is a bad site ADDRESS, not a provider
        // failure. Load it as a site-unavailable state so the correct why/how
        // notice renders instead of the (wrong) provider-key guidance.
        // UAT-006: SITE_GEOCODE_LOW_CONFIDENCE is the same class — a bad/
        // incomplete site address — handled as a verify-manually state.
        if (data.error?.code === 'SITE_NOT_GEOCODED' || data.error?.code === 'SITE_GEOCODE_LOW_CONFIDENCE') {
          setLookup({ kind: 'loaded', data });
          return;
        }
        if (data.configured === false || data.error) {
          const err = data.error ?? {
            code: 'PROVIDER_NOT_CONFIGURED',
            message: 'Emergency-contact lookup is not configured on the server.',
          };
          setLookup({ kind: 'provider-error', code: err.code, message: err.message });
          return;
        }
        setLookup({ kind: 'loaded', data });
      } catch (err) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : String(err);
        setLookup({
          kind: 'provider-error',
          code: 'LOOKUP_REQUEST_FAILED',
          message: `The emergency-contacts lookup request failed: ${message}`,
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  // Device location: optional and free (browser geolocation, no Google API).
  // IND-JHA-02: request EXACTLY ONCE (the effect re-ran on the loading→loaded
  // transition and asked the browser twice).
  const geoRequestedRef = useRef(false);
  useEffect(() => {
    if (lookup.kind !== 'loaded' || geoRequestedRef.current) return;
    geoRequestedRef.current = true;
    if (!('geolocation' in navigator) || !navigator.geolocation) {
      setGeo('unavailable');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setDevicePos({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setGeo('granted');
      },
      () => setGeo('denied'),
      { timeout: 8000, maximumAge: 60000 }
    );
  }, [lookup.kind]);

  // JHA-MED-02: render the device pin even when permission arrives after the
  // map finished initialising. IND-JHA-02: a newer fix MOVES the marker instead
  // of leaving it stale at the first coordinates.
  useEffect(() => {
    if (mapState !== 'ready' || !devicePos || !mapInstanceRef.current) return;
    const MarkerCtor = mapsApiRef.current?.Marker;
    if (!MarkerCtor) return;
    if (!deviceMarkerRef.current) {
      deviceMarkerRef.current = new MarkerCtor({
        map: mapInstanceRef.current,
        position: devicePos,
        title: 'Your location (device)',
      });
    } else if (typeof deviceMarkerRef.current.setPosition === 'function') {
      deviceMarkerRef.current.setPosition(devicePos);
    }
  }, [mapState, devicePos]);

  const loaded = lookup.kind === 'loaded' ? lookup.data : null;
  // UAT-006 fail closed: a low-confidence site geocode means the provider
  // could NOT resolve the street address — "nearby" facilities could be miles
  // from the real site, so NOTHING is named (live or saved); the user gets a
  // verify-manually state instead.
  const lowConfidenceGeocode =
    loaded?.siteGeocodeConfidence === 'low' || loaded?.error?.code === 'SITE_GEOCODE_LOW_CONFIDENCE';
  const { facility: rawFacility, droppedNonMedical } = nearestMedicalFacility(loaded, emergencyInfo);
  const facility = lowConfidenceGeocode ? null : rawFacility;
  // A site fix is REAL only if it validates (NEW-JHA-05); when it does, the
  // medical searches ran (F-01: an ungeocodable site never searched).
  const siteValid = !!loaded && isValidGeoPoint(loaded.coordinates);
  // NEW-JHA-04: "no medical facility" is about USABLE ones — the card/pin logic
  // all require a name, so a nameless entry must not suppress this warning.
  const medicalEmpty = !!loaded && siteValid && !hasUsableName(loaded.hospital) && !hasUsableName(loaded.clinic);
  const directions = directionsUrl(facility);
  const medicalPins: EmergencyFacility[] = [];
  if (!lowConfidenceGeocode && hasUsableName(loaded?.hospital) && isPlausibleMedicalFacilityName(loaded!.hospital!.name)) medicalPins.push(loaded!.hospital!);
  if (!lowConfidenceGeocode && hasUsableName(loaded?.clinic) && isPlausibleMedicalFacilityName(loaded!.clinic!.name)) medicalPins.push(loaded!.clinic!);
  // UAT-006: show the verify-manually state when the site fix is coarse OR
  // when non-medical results were withheld (server flag or client filter).
  const needsManualVerification = lowConfidenceGeocode || droppedNonMedical || !!loaded?.nonMedicalResults;

  // Lazy map load — only when a key exists, the lookup succeeded, and we have
  // somewhere to put it. Pin visibility does not depend on the map.
  useEffect(() => {
    if (!mapsKey || lookup.kind !== 'loaded' || mapState !== 'idle') return undefined;
    setMapState('loading');
    let cancelled = false;
    loadGoogleMaps(mapsKey)
      .then((maps) => {
        if (cancelled || !mapDivRef.current) return;
        const m = maps as {
          Map: new (el: HTMLElement, opts: Record<string, unknown>) => unknown;
          Marker: new (opts: Record<string, unknown>) => unknown;
        };
        // JHA-MED-01: Maps JS wants { lat, lng } — the server sends
        // { latitude, longitude }; convert at this boundary.
        // IND-JHA-01 / NEW-JHA-05: with no VALID site coordinates there is NO
        // honest 'Job site' pin — the US-center fallback is a map center only,
        // never a marker, and malformed coordinate objects don't count.
        const hasSite = siteValid;
        const center = hasSite
          ? toLatLngLiteral(loaded!.coordinates!)
          : { lat: 39.5, lng: -98.35 };
        const map = new m.Map(mapDivRef.current, {
          center,
          zoom: hasSite ? 12 : 4,
          mapTypeControl: false,
          fullscreenControl: false,
          streetViewControl: false,
        });
        mapInstanceRef.current = map;
        mapsApiRef.current = m;
        if (hasSite) new m.Marker({ map, position: center, title: 'Job site' });
        for (const pin of medicalPins) {
          if (pin.latitude !== null && pin.longitude !== null) {
            new m.Marker({
              map,
              position: { lat: pin.latitude, lng: pin.longitude },
              title: pin.name ?? 'Emergency facility',
            });
          }
        }
        setMapState('ready');
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setMapState('error');
        console.error('Google Maps failed to load:', err.message);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookup.kind, mapsKey]);

  return (
    <div
      data-testid="jha-emergency-map"
      className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 print:shadow-none print:border-gray-300"
    >
      <div className="flex items-center gap-3 mb-4">
        <MapPin className="h-6 w-6 text-red-600" />
        <h2 className="text-lg sm:text-xl font-bold text-gray-900">Emergency Map &amp; Directions</h2>
      </div>

      {/* Offline fallback — always visible, independent of map and keys. */}
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
        <p className="text-sm font-semibold text-red-900 mb-1">Nearest emergency facility</p>
        {facility ? (
          <>
            <p className="text-base font-bold text-red-900">{facility.name}</p>
            {facility.address && <p className="text-sm text-red-800">{facility.address}</p>}
            {facility.phone && (
              <p className="text-sm text-red-800 font-semibold">
                <a href={`tel:${facility.phone}`} className="inline-flex items-center gap-1">
                  <Phone className="h-4 w-4" /> {facility.phone}
                </a>
              </p>
            )}
          </>
        ) : (
          <p className="text-sm text-red-800">
            No emergency facility on record for this JHA. In an emergency call 911.
          </p>
        )}
        <p className="text-sm text-red-800 mt-1 font-semibold">Life-threatening emergency: call 911.</p>
        {directions && (
          <a
            href={directions}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="jha-directions-link"
            className="mt-3 inline-flex items-center gap-2 px-4 py-2.5 bg-red-600 text-white rounded-lg font-semibold text-base active:bg-red-700"
          >
            <Navigation className="h-5 w-5" />
            Directions to {facility?.name ?? 'emergency facility'}
          </a>
        )}
      </div>

      {lookup.kind === 'provider-error' && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 mb-4" data-testid="jha-map-provider-error">
          <p className="flex items-center gap-2 font-semibold text-amber-900">
            <AlertTriangle className="h-5 w-5" /> Emergency-place lookup failed
          </p>
          <p className="text-sm text-amber-900 mt-1">
            Why: the emergency-contacts provider could not be used ({lookup.code}). {lookup.message}
          </p>
          <p className="text-sm text-amber-900">
            How to fix: an admin must configure a provider key on the server (GOOGLE_MAPS_API_KEY or
            MAPBOX_API_KEY) and verify the project site address geocodes. The static facility above and
            911 remain valid.
          </p>
        </div>
      )}

      {/* IND-JHA-01 / NEW-JHA-05: loaded-but-no-VALID-site-coordinates must fail
          visibly — never label a fallback center as the job site. */}
      {lookup.kind === 'loaded' && !siteValid && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 mb-4" data-testid="jha-map-site-unavailable">
          <p className="flex items-center gap-2 font-semibold text-amber-900">
            <AlertTriangle className="h-5 w-5" /> Job-site location unavailable
          </p>
          <p className="text-sm text-amber-900 mt-1">
            Why: the job-site address could not be turned into map coordinates, so the map cannot show a
            job-site pin (only a wide-area view).
          </p>
          <p className="text-sm text-amber-900">
            How to fix: verify the project site address (street, city, state, ZIP) and re-open this page.
            The facility list and directions below do not depend on it. In an emergency call 911.
          </p>
        </div>
      )}

      {/* UAT-006 fail closed: no facility could be verified for this site —
          name nothing, say WHY, and require manual verification instead. */}
      {lookup.kind === 'loaded' && needsManualVerification && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 mb-4" data-testid="jha-map-verify-manually">
          <p className="flex items-center gap-2 font-semibold text-amber-900">
            <AlertTriangle className="h-5 w-5" /> Verify the nearest emergency facility manually
          </p>
          <p className="text-sm text-amber-900 mt-1">
            {lowConfidenceGeocode
              ? 'Why: the job-site address could only be matched as an approximate location (the street address could not be resolved), so naming a "nearby" emergency facility would be unsafe — no facility is named.'
              : 'Why: the place lookup returned results that could not be verified as actual medical facilities (e.g. a business mis-categorised as a hospital), so they were withheld rather than shown as safety data.'}
          </p>
          <p className="text-sm text-amber-900">
            How to fix: verify the project site address (street, city, state, ZIP) and look up contacts again;
            confirm the nearest hospital/urgent care with your crew manually before work begins.
            In an emergency call 911.
          </p>
        </div>
      )}

      {/* DELTA2-02: the server flags partial/empty lookups — surface them loudly.
          IND-JHA-03: zero hospital AND zero urgent care is surfaced even when the
          server's allLookupsEmpty stays false because police/fire succeeded. */}
      {loaded?.providerDegraded && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 mb-4" data-testid="jha-map-provider-degraded">
          <p className="flex items-center gap-2 font-semibold text-amber-900">
            <AlertTriangle className="h-5 w-5" /> Some emergency lookups failed
          </p>
          <p className="text-sm text-amber-900 mt-1">
            Why: one or more emergency-place lookups failed at the provider, so the facility list below
            may be incomplete.
          </p>
          <p className="text-sm text-amber-900">
            How to fix: re-open this page to retry. If it persists, an admin should verify the provider
            key and the project site address, and the crew should confirm the nearest facility manually.
            In an emergency call 911.
          </p>
        </div>
      )}
      {(loaded?.allLookupsEmpty || medicalEmpty) && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 mb-4" data-testid="jha-map-all-empty">
          <p className="flex items-center gap-2 font-semibold text-amber-900">
            <AlertTriangle className="h-5 w-5" /> No emergency facilities found near this site
          </p>
          <p className="text-sm text-amber-900 mt-1">
            Why: no named hospital or urgent care could be shown near the job-site address (a result
            without a name is never displayed).
          </p>
          <p className="text-sm text-amber-900">
            How to fix: verify the nearest hospital/urgent care manually before work begins (the saved
            facility above still applies if one is on record). In an emergency call 911.
          </p>
        </div>
      )}

      {medicalPins.length > 0 && (
        <div className="mb-4" data-testid="jha-map-pin-list">
          <p className="text-sm font-semibold text-gray-700 mb-2">Nearby emergency facilities</p>
          <ul className="space-y-1">
            {medicalPins.map((pin, i) => (
              <li key={i} className="text-sm text-gray-900 flex items-start gap-2">
                <MapPin className="h-4 w-4 mt-0.5 text-red-600 flex-shrink-0" />
                <span>
                  <span className="font-medium">{pin.name}</span>
                  {pin.address && <span className="text-gray-600"> — {pin.address}</span>}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {!mapsKey ? (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4" data-testid="jha-map-not-configured">
          <p className="text-sm text-gray-800">
            The live map is not configured. Why: no browser map key (VITE_GOOGLE_MAPS_API_KEY) is set in
            the client environment, so the Google map cannot render. How to fix: add a Google Maps
            JavaScript API browser key restricted by HTTP referrer to conformara.com and set
            VITE_GOOGLE_MAPS_API_KEY. The facility list, directions link, and 911 above do not depend on
            the map.
          </p>
        </div>
      ) : (
        <>
          {mapState === 'error' && (
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 mb-4" data-testid="jha-map-load-error">
              <p className="flex items-center gap-2 font-semibold text-amber-900">
                <AlertTriangle className="h-5 w-5" /> The map failed to load
              </p>
              <p className="text-sm text-amber-900 mt-1">
                Why: the Google Maps JavaScript script could not load (network/ad-blocker/referrer
                restriction on the browser key). How to fix: check connectivity and that the key&apos;s
                HTTP-referrer restrictions include this domain. The facility list, directions link, and
                911 remain usable.
              </p>
            </div>
          )}
          <div className="relative rounded-lg overflow-hidden border border-gray-200" style={{ height: 260 }}>
            {mapState !== 'ready' && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-50 text-sm text-gray-500">
                <MapIcon className="h-5 w-5 mr-2" />
                {mapState === 'loading' ? 'Loading map…' : 'Map unavailable'}
              </div>
            )}
            <div ref={mapDivRef} data-testid="jha-google-map" className="w-full h-full" />
          </div>
          {geo === 'denied' && (
            <p className="mt-2 text-xs text-gray-500" data-testid="jha-geo-denied">
              Location sharing was declined — the map still shows the job site and nearby emergency
              facilities. You can re-enable location in your browser settings.
            </p>
          )}
          {geo === 'unavailable' && (
            <p className="mt-2 text-xs text-gray-500" data-testid="jha-geo-unavailable">
              This device or browser does not support location sharing — the map still shows the job
              site and nearby emergency facilities.
            </p>
          )}
        </>
      )}
    </div>
  );
}
